import { Plus, Search, Star } from "lucide-react";

function ScoreRing({ score, accent }) {
  const r = 16;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative w-11 h-11 flex-shrink-0">
      <svg className="w-11 h-11 -rotate-90" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r={r} fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
        <circle
          cx="20"
          cy="20"
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth="3.5"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-700">
        {score}%
      </span>
    </div>
  );
}

export default function TrovaPreviewDashboard({ config }) {
  const isDark = config.theme === "personale";
  const accent = isDark ? "#7c3aed" : "#0f766e";
  const accentSoft = isDark ? "bg-violet-50 text-violet-700" : "bg-teal-50 text-teal-800";
  const btnPrimary = isDark
    ? "bg-violet-600 hover:bg-violet-500 text-white"
    : "bg-teal-700 hover:bg-teal-600 text-white";

  return (
    <div className="min-h-screen bg-slate-100 pointer-events-none select-none" aria-hidden="true">
      <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {config.stats.map((s) => (
            <div
              key={s.label}
              className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm"
            >
              <p className="text-xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs font-medium text-slate-700 mt-0.5">{s.label}</p>
              <p className="text-[11px] text-slate-400 mt-1">{s.hint}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${btnPrimary}`}>
            <Plus className="w-4 h-4" />
            {isDark ? "Cerca personale" : "Cerca Ospite"}
          </span>
          <span className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium border border-slate-300 bg-white text-slate-700">
            {isDark ? "Richiedi candidatura" : "Ho un posto disponibile"}
          </span>
          <span className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium border border-slate-300 bg-white text-slate-700">
            {isDark ? "Condividi annuncio" : "Richiedi documenti"}
          </span>
          <span className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium border border-slate-300 bg-white text-slate-700">
            {isDark ? "Le mie richieste" : "Condividi in rete"}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <div className="h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 flex items-center text-sm text-slate-400">
              Cerca…
            </div>
          </div>
          {(isDark
            ? ["Tutti", "Compatibili", "Disponibili subito", "OSS", "Infermieri"]
            : ["Tutte", "Compatibili", "Urgenti", "Partner", "Nuove"]
          ).map((f, i) => (
            <span
              key={f}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border ${
                i === 0
                  ? isDark
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-teal-700 text-white border-teal-700"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              {f}
            </span>
          ))}
        </div>

        <div className="mt-4 grid lg:grid-cols-[1fr_360px] gap-4">
          <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
            {config.previewRows.map((row, idx) => (
              <div
                key={row.name}
                className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 ${
                  idx === 0 ? (isDark ? "bg-violet-50/60" : "bg-teal-50/50") : ""
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white ${
                    isDark ? "bg-violet-500" : "bg-teal-600"
                  }`}
                >
                  {row.name
                    .split(" ")
                    .map((p) => p[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900 truncate">{row.name}</p>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${accentSoft}`}>
                      {row.badge}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{row.meta}</p>
                </div>
                <ScoreRing score={row.score} accent={accent} />
              </div>
            ))}
          </div>

          <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 hidden lg:block">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-base font-bold text-slate-900">{config.previewRows[0].name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{config.previewRows[0].meta}</p>
              </div>
              <ScoreRing score={config.previewRows[0].score} accent={accent} />
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4 mb-2">
              Informazioni principali
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {(isDark
                ? [
                    ["Età", "34 anni"],
                    ["Esperienza", "6 anni"],
                    ["Patente", "B"],
                    ["Disponibilità", "Immediata"],
                    ["Contratto", "Full time"],
                    ["Turni", "Mattino, Pomeriggio"],
                  ]
                : [
                    ["Età", "82 anni"],
                    ["Sesso", "M"],
                    ["Condizione", "Alzheimer"],
                    ["Ingresso", "Entro 15 gg"],
                    ["Budget", "1.800 €/mese"],
                    ["Zona", "Guidonia (RM)"],
                  ]
              ).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 px-2.5 py-2">
                  <p className="text-slate-400">{k}</p>
                  <p className="font-medium text-slate-800 mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            <div
              className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
                isDark ? "border-violet-200 text-violet-700" : "border-teal-200 text-teal-800"
              }`}
            >
              <Star className="w-4 h-4" />
              Aggiungi ai preferiti
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
